import React from "react";
import Header from "../sharedComponents/header/header";
import "./questionsToOffer.scss";
import { CalculateStore } from "../../stores/calculateStore";
import { inject, observer } from "mobx-react";
import { PropertyTypeId, PropertyTypes } from "../../helpers/propertyType";
import { QuestionsToOfferTooltips } from "../../helpers/questionToOfferTooltips";
import Tile from "../sharedComponents/tile/tile";
import { Floor, FloorId } from "../../helpers/floor";
import { TileModel } from "../../helpers/tile";
import NumberInput from "../sharedComponents/numberInput/numberInput";
import NextButton from "../sharedComponents/nextButton/nextButton";
import { ErrorMessage } from "../../helpers/errorMessage";
import { UiFlagsStore } from "../../stores/uiFlagsStore";
import Notification from "../sharedComponents/notification/notification";
import { Widget } from "../../helpers/widget";
import BackButton from "../sharedComponents/backButton/backButton";
import OverhaulYear from "./overhaulYear/overhaulYear";
import Address from "../sharedComponents/address/address";
import NoYesRadioGroup from "../sharedComponents/noYesRadioGroup/noYesRadioGroup";
import DropDown from "../sharedComponents/dropDown/dropDown";
import { OwnershipTypeId, OwnershipTypes } from "../../helpers/ownership";
import { ClientStore } from "../../stores/clientStore";
import { PackageId, PackageModel } from "../../helpers/package";
import { Risk, RisksId } from "../../helpers/risk";
import { PolicyStore } from "../../stores/policyStore";
import ReactGA from "react-ga4";

interface QuestionsToOfferProps {
  calculateStore?: CalculateStore;
  uiFlagsStore?: UiFlagsStore;
  clientStore?: ClientStore;
  policyStore?: PolicyStore;
}

interface QuestionsToOfferState {
  displayFloor: boolean;
  propertyTypeHasError: boolean;
  floorHasError: boolean;
}

@inject("calculateStore", "uiFlagsStore", "clientStore", "policyStore")
@observer
export default class QuestionsToOffer extends React.Component<QuestionsToOfferProps, QuestionsToOfferState> {
  constructor(props: any) {
    super(props);

    this.state = {
      displayFloor: false,
      propertyTypeHasError: false,
      floorHasError: false
    };
  }

  public componentDidMount() {
    this.setState({ displayFloor: this.props.calculateStore!.isFlat });
    if (this.props.uiFlagsStore!.isDirect) {
      this.props.calculateStore!.setPropertyInsurance(true);
    }
    this.handleGaTag("questionsToOfferEntry");
  }

  public render() {
    return (
      <div className="content questions-to-offer">
        <Header type="big"
          mainHeader="Pytania do oferty"
          additionalInfo="Wypełnij poniższy formularz. Dzięki tym informacjom przygotujemy ofertę dopasowaną do Ciebie."
        />

        {this.renderPropertyTypes()}

        {this.state.displayFloor && (
          this.renderFloor()
        )}

        {this.renderArea()}
        {this.renderConstructionYear()}
        {this.renderOverhaulYear()}
        {this.renderAddress()}
        {this.renderFlammableBuildingStructure()}
        {this.renderOwnershipType()}
        {this.renderRegulation()}
        <div
          className={"buttons " + (this.props.uiFlagsStore!.isDirect ? "other-than-sobol" : "")}>
          {!this.props.uiFlagsStore!.isDirect &&
            <BackButton onClick={() => this.backButtonOnClick()} />}
          <NextButton onClick={() => this.nextButtonOnClick()}
            buttonType="next"
          />
        </div>
      </div>
    );
  }

  private renderPropertyTypes() {
    const flats: JSX.Element[] = [];
    const houses: JSX.Element[] = [];

    PropertyTypes.forEach((propertyType: TileModel) => {
      const isSelected = this.props.calculateStore!.estate.propertyType === propertyType.id;
      const renderedPropertyType = propertyType.id.includes("FLAT") ? flats : houses;

      renderedPropertyType.push(
        <Tile key={propertyType.id}
          tile={propertyType}
          isSelected={isSelected}
          hasError={this.state.propertyTypeHasError}
          onClick={() => {
            this.changePropertyType(propertyType.id);
          }}
        />
      );
    });

    return (
      <div className="section">
        <Header type="medium" mainHeader="Rodzaj nieruchomości"
          tooltip={QuestionsToOfferTooltips.PROPERTY_TYPE} />

        {this.state.propertyTypeHasError && (
          <Notification type="error" className="tile-notification property-type"
            message="Wybierz rodzaj nieruchomości" />
        )}

        <div className="property-container">
          <div className="flats">
            {flats}
          </div>
          <div className="homes">
            {houses}
          </div>
        </div>
      </div>
    );
  }

  private renderFloor() {
    const floor: JSX.Element[] = [];

    Floor.forEach((item: TileModel) => {
      const isSelected = this.props.calculateStore!.estate.floor === item.id;

      floor.push(
        <Tile key={item.id}
          tile={item}
          isSelected={isSelected}
          hasError={this.state.floorHasError}
          onClick={() => {
            this.changeFloor(item.id);
          }}
        />
      );
    });

    return (
      <div className="section">
        <Header type="medium" mainHeader="Piętro" />

        {this.state.floorHasError && (
          <Notification type="error" className="tile-notification floor"
            message="Wybierz piętro" />
        )}

        <div className="floor-container">
          {floor}
        </div>
      </div>
    );
  }

  private renderArea() {
    return (
      <div className="section">
        <div className="area-container input-container">
          <Header type="medium" mainHeader="Powierzchnia użytkowa"
            tooltip={QuestionsToOfferTooltips.AREA} />
          <div className="area-and-unit-container">
            <NumberInput
              validatedValue={this.props.calculateStore!.estate.area}
              formatted={true}
              topLabel="Powierzchnia użytkowa"
            />
            <span className="unit">m<sup>2</sup></span>
          </div>
        </div>
      </div>
    );
  }

  private renderConstructionYear() {
    return (
      <div className="section">
        <div className="input-container construction-year-container">
          <Header type="medium" mainHeader="Rok budowy"
            tooltip={QuestionsToOfferTooltips.CONSTRUCTION_YEAR} />
          <NumberInput
            validatedValue={this.props.calculateStore!.estate.constructionYear}
            onChange={(constructionYear) => {
              this.changeConstructionYear(constructionYear);
            }}
            formatted={false}
            maxLength={4}
            topLabel="Rok budowy"
          />
        </div>
      </div>
    );
  }

  private renderOverhaulYear() {
    return (
      <div className="section">
        <div className="input-container overhaul-year-container">
          <Header type="medium" mainHeader="Remont generalny"
            tooltip={QuestionsToOfferTooltips.OVERHAUL_YEAR} />
          <OverhaulYear value={this.props.calculateStore!.estate.overhaulYear} />
        </div>
      </div>
    );
  }

  private renderAddress() {
    const estate = this.props.calculateStore!.estate;
    return (
      <div className="section">
        <div className="input-container address-container">
          <Header type="medium" mainHeader="Adres nieruchomości"
            tooltip={QuestionsToOfferTooltips.ADDRESS} />
          <Address
            address={estate.address}
            validateApartmentNumber={
              estate.propertyType === PropertyTypeId.flat
              || estate.propertyType === PropertyTypeId.flatUnderConstructionClosedState} />
        </div>
      </div>
    );
  }

  private renderFlammableBuildingStructure() {
    return (
      <div className="section">
        <div className="input-container flammable-building-structure-container">
          <Header type="medium"
            mainHeader="Konstrukcja budynku lub pokrycie dachu wykonane są z elementów łatwopalnych"
            tooltip={QuestionsToOfferTooltips.FLAMMABLE_STRUCTURE}
          />
          <NoYesRadioGroup
            name="flammableBuildingStructure"
            value={this.props.calculateStore!.estate.flammableBuildingStructure}
            onChange={(newValue) => this.changeFlammableBuildingStructure(newValue)} />
        </div>
      </div>
    );
  }

  private renderOwnershipType() {
    return (
      <div className="section">
        <div className="input-container ownership-type-container">
          <Header type="medium" mainHeader={OwnershipTypes.label}
            tooltip={QuestionsToOfferTooltips.OWNERSHIP_TYPE} />
          <DropDown
            onChange={(newValue) => {
              this.changeOwnershipType(newValue);
            }}
            value={this.props.calculateStore!.estate.ownershipType}
            items={OwnershipTypes.dropDown}
          />
        </div>
      </div>
    );
  }

  private renderRegulation() {
    return this.props.uiFlagsStore!.isDirect ?
      <div className="regulation">
        Skorzystanie z usługi jest jednoznaczne z akceptacją <a
          href="https://tuz.pl/strefa-klienta/regulaminy/"
          target="_blank" rel="noopener noreferrer"
          className="link-to-file">Regulaminu świadczenia usług drogą elektroniczną.</a>
      </div>
      : null;
  }

  private changePropertyType(id: string) {
    const propertyTypeId: string[] = Object.values(PropertyTypeId);
    this.setState({ propertyTypeHasError: false });

    if (propertyTypeId.includes(id)) {
      const beforePropertyType = this.props.calculateStore!.estate.propertyType;
      const beforeIsPropertyUnderConstruction = this.props.calculateStore!.isPropertyUnderConstruction();
      this.props.calculateStore!.estate.setPropertyType(id as PropertyTypeId);
      const afterIsPropertyUnderConstruction = this.props.calculateStore!.isPropertyUnderConstruction();

      const isPackageRangeChanged = Risk.isPropertyTypesHaveRiskDifference(beforePropertyType, this.props.calculateStore!.estate.propertyType);

      if (beforeIsPropertyUnderConstruction !== afterIsPropertyUnderConstruction || !beforePropertyType || isPackageRangeChanged) {
        this.props.calculateStore!.resetPackages();
      }

      this.setState({
        displayFloor: this.props.calculateStore!.isFlat
      });
    }
  }

  private changeFloor(id: string) {
    const floorId: string[] = Object.values(FloorId);
    this.setState({ floorHasError: false });

    if (floorId.includes(id)) {
      this.props.calculateStore!.estate.setFloor(id as FloorId);
    }
  }

  private changeConstructionYear(constructionYear: number) {
    this.props.calculateStore!.estate.overhaulYear.validate();
  }

  private changeFlammableBuildingStructure(value: boolean) {
    this.props.calculateStore!.estate.setFlammableBuildingStructure(value);
  }

  private changeOwnershipType(value: OwnershipTypeId) {
    this.props.calculateStore!.estate.setOwnershipType(value);
  }

  private validateForm() {
    this.setState({ propertyTypeHasError: this.props.calculateStore!.estate.propertyType === null });
    this.setState({ floorHasError: this.state.displayFloor && this.props.calculateStore!.estate.floor === null });

    this.props.calculateStore!.estate.area.validate();
    this.props.calculateStore!.estate.constructionYear.validate();
    this.props.calculateStore!.estate.overhaulYear.validate();
    this.props.calculateStore!.estate.address.validate();
    this.props.calculateStore!.estate.ownershipType.validate();
  }

  private goToNextStep() {
    Widget.goToNextStep(this.props.uiFlagsStore!);
  }

  private nextButtonOnClick() {
    this.props.calculateStore!.calculationRequestStatus.reset();

    this.validateForm();

    if (ErrorMessage.verifyIfErrorMessageIsRendered()) {
      ErrorMessage.scrollToFirstErrorMessage();
    } else {
      if (!this.props.calculateStore!.isFlat) {
        this.props.calculateStore!.resetFloor();
      }
      this.props.calculateStore!.resetCalculationResponse();
      this.props.calculateStore!.refreshCalcultionRequire();
      this.clearInsuredSumDetachedHouseOrFlatWithFixedElements();
      this.goToNextStep();
    }
  }

  private clearInsuredSumDetachedHouseOrFlatWithFixedElements() {
    const isPolicyRenewal = this.props.policyStore?.isPolicyRenewal;

    if (!isPolicyRenewal) {
      this.props.calculateStore!
        .packages.forEach((packageData: PackageModel) => {
          if (this.shouldNotResetInsuranceSumForSelectedPackageSaveCalculation(packageData.id)) {
            packageData.risks.get(RisksId.detachedHouseOrFlatWithFixedElements)!.insuranceSum = undefined;
          }
        });
    }
  }

  private shouldNotResetInsuranceSumForSelectedPackageSaveCalculation(packageId: PackageId): boolean {
    return !!this.props.calculateStore?.parentCalculationId && this.props.calculateStore.selectedPackage !== packageId;
  }

  private backButtonOnClick() {
    Widget.goToPrevStep(this.props.uiFlagsStore!);
    const tuzApkManager = document.querySelector('tuz-apk-manager') as HTMLElement;
    if (tuzApkManager) {
      tuzApkManager.style.display = '';
    }
  }

  private handleGaTag(action:string) {
    if (this.props.uiFlagsStore?.isDirect) {
      ReactGA.event({category: "event", action: `${action}`});
    }
  }
}
